// UI-side goal health. The classification RULE is NOT defined here — it lives
// once in `@excellent/core` (`classifyGoalHealth`), the same function the MCP
// reconciler uses to decide what to auto-close. This module only (a) wraps that
// rule with a `settled` verdict for non-active goals and (b) attaches the UI
// presentation (label / intent / hint). Single source of truth → the badge can
// never disagree with what the background reconciler actually does.
//
//   settled — the goal is no longer active (done/paused/abandoned); the
//             reconciler does not touch it. (The four active-goal buckets —
//             needs-plan / in-progress / ready / needs-review — are documented
//             on `classifyGoalHealth` in @excellent/core.)

// RO-3-02 / RO-4-04: the bare `@excellent/core` barrel chains
// index → objectives → events/engine → db → security/{keystore,keychain} — a
// client bundle importing it ships the SQLCipher key path + OS keychain to
// the browser (PRD §5 I4, risk R14). `classifyGoalHealth` is a pure, zero-
// dependency function (`packages/core/src/goal-health.ts` — no DB, no I/O),
// so it gets its own additive subpath export instead of dragging the whole
// barrel in for one function. Discovered empirically: `pnpm build:client`
// failed on a Node-builtin import from `security/keystore.js` the moment this
// module (reached via `GoalsLedger`) entered the client bundle.
import { classifyGoalHealth, type GoalHealth as CoreGoalHealth } from "@excellent/core/goal-health";
import type { Intent } from "../../tokens";

export type GoalHealth = CoreGoalHealth | "settled";

export interface GoalHealthInput {
  status: "active" | "paused" | "done" | "abandoned";
  task_count: number;
  done_task_count: number;
  cancelled_task_count: number;
}

export function goalHealth(g: GoalHealthInput): GoalHealth {
  if (g.status !== "active") return "settled";
  return classifyGoalHealth({
    task_count: g.task_count,
    done_count: g.done_task_count,
    cancelled_count: g.cancelled_task_count,
  });
}

export interface GoalHealthMeta {
  label: string;
  intent: Intent;
  /** One-line plain-English description for tooltips / detail copy. */
  hint: string;
}

export const GOAL_HEALTH_META: Record<GoalHealth, GoalHealthMeta> = {
  "needs-plan": {
    label: "Needs plan",
    intent: "warning",
    hint: "No linked issues yet — break this goal into issues so its completion can be tracked.",
  },
  "in-progress": {
    label: "In progress",
    intent: "info",
    hint: "Linked issues are still open. The goal completes when every issue is done.",
  },
  ready: {
    label: "Ready to close",
    intent: "success",
    hint: "Every linked issue is done (each verified via ship/verify). The reconciler will close this goal — or close it now.",
  },
  "needs-review": {
    label: "Needs review",
    intent: "warning",
    hint: "Every linked issue was cancelled — nothing was accomplished. Re-plan it into fresh issues, or abandon the goal.",
  },
  settled: {
    label: "Settled",
    intent: "neutral",
    hint: "This goal is no longer active.",
  },
};

/** done / total completion fraction (0–1). Cancelled issues count as resolved
 *  toward the denominator's terminal share but not as "done". */
export function goalProgress(g: {
  task_count: number;
  done_task_count: number;
}): number {
  if (g.task_count === 0) return 0;
  return g.done_task_count / g.task_count;
}
